import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function Settings({ user }) {
  const [fields, setFields] = useState([]);
  const [required, setRequired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const privileges = user?.privileges || [];
  const canEdit = user?.role?.toLowerCase() === 'admin' || privileges.includes('*') || privileges.includes('settings');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/customer-fields`, authHeaders());
      setFields(res.data?.fields || []);
      setRequired(res.data?.required || []);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key, on) => {
    setRequired(on ? [...required, key] : required.filter((f) => f !== key));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/customer-fields`, { required }, authHeaders());
      setRequired(res.data?.required || []);
      toast.success('Onboarding settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">System configuration</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Customer Onboarding — Required Fields</CardTitle>
          <p className="text-sm text-slate-600">
            Turn a field on to make it mandatory when onboarding or editing a customer.
            Everything left off can be saved blank and filled in later.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-1">
              {fields.map((field) => (
                <div key={field.key} className="flex items-center justify-between py-3 border-b last:border-0">
                  <Label htmlFor={`field-${field.key}`} className="font-normal text-slate-700">
                    {field.label}
                  </Label>
                  <Switch
                    id={`field-${field.key}`}
                    data-testid={`required-${field.key}`}
                    checked={required.includes(field.key)}
                    disabled={!canEdit}
                    onCheckedChange={(on) => toggle(field.key, on)}
                  />
                </div>
              ))}
              {canEdit ? (
                <div className="pt-4">
                  <Button onClick={save} disabled={saving} data-testid="save-settings-button">
                    {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                    Save Settings
                  </Button>
                  {required.length === 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      No field is mandatory — staff can onboard a customer with whatever details they have.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500 pt-4">You do not have permission to change these settings.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
